import {
    LS_CUSTOMIZATION_KEY,
    LS_CUSTOMIZATION_SET_EVENT_KEY,
    LS_FOOTPRINT_KEY,
    LS_WAITING_TEXT_KEY,
} from "constants/constants";
import { CSS_VARIABLES } from "enums/cssVariables";
import { CustomizationBackground } from "types/customizationType";

export type CustomizationProps = {
    logoAlignment: string;
    titleColor: string;
    titleBackgroundColor: string;
    buttonColor: string;
    buttonBackgroundColor: string;
    logoUrl: string;
    installationButtonDisplay: boolean;
    backgroundUrl?: string;
    connectionImageUrl?: string;
    voteImageUrl?: string;
    voteBackgroundOpacity?: string;
};

const CUSTOMIZATION_PROP_LOGO_URL = "logoUrl";
const CUSTOMIZATION_PROP_LOGO_ALIGNMENT = "logoAlignment";
const CUSTOMIZATION_PROP_BUTTON_BACKGROUND_COLOR = "buttonBackgroundColor";
const CUSTOMIZATION_PROP_BUTTON_COLOR = "buttonColor";
const CUSTOMIZATION_PROP_TITLE_BACKGROUND_COLOR = "titleBackgroundColor";
const CUSTOMIZATION_PROP_TITLE_COLOR = "titleColor";
const CUSTOMIZATION_PROP_INSTALLATION_BUTTON_DISPLAY =
    "installationButtonDisplay";
const CUSTOMIZATION_PROP_BACKGROUND_URL = "backgroundUrl";
const CUSTOMIZATION_PROP_CONNECTION_IMAGE_URL = "connectionImageUrl";
const CUSTOMIZATION_PROP_VOTE_IMAGE_URL = "voteImageUrl";
const CUSTOMIZATION_PROP_VOTE_BACKGROUND_OPACITY = "voteBackgroundOpacity";

const FULL_CSS_OPACITY = "1";
const MIN_OPACITY_PERCENTAGE = 0;
const MAX_OPACITY_PERCENTAGE = 100;

const customizationPropsToCssVariablesMapping = new Map<string, string>([
    [CUSTOMIZATION_PROP_TITLE_COLOR, CSS_VARIABLES.PRIMARY_TEXT_COLOR],
    [
        CUSTOMIZATION_PROP_TITLE_BACKGROUND_COLOR,
        CSS_VARIABLES.BACKGROUND_FORM_COLOR,
    ],
    [CUSTOMIZATION_PROP_BUTTON_COLOR, CSS_VARIABLES.PRIMARY_BUTTON_TEXT_COLOR],
    [
        CUSTOMIZATION_PROP_BUTTON_BACKGROUND_COLOR,
        CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR,
    ],
    [CUSTOMIZATION_PROP_LOGO_ALIGNMENT, CSS_VARIABLES.LOGO_ALIGNMENT],
    [CUSTOMIZATION_PROP_LOGO_URL, CSS_VARIABLES.LOGO_URL],
    [
        CUSTOMIZATION_PROP_INSTALLATION_BUTTON_DISPLAY,
        CSS_VARIABLES.INSTALLATION_BUTTON_DISPLAY,
    ],
    [CUSTOMIZATION_PROP_BACKGROUND_URL, CSS_VARIABLES.BACKGROUND_IMAGE_URL],
    [
        CUSTOMIZATION_PROP_CONNECTION_IMAGE_URL,
        CSS_VARIABLES.CONNECTION_IMAGE_URL,
    ],
    [CUSTOMIZATION_PROP_VOTE_IMAGE_URL, CSS_VARIABLES.VOTE_IMAGE_URL],
    [
        CUSTOMIZATION_PROP_VOTE_BACKGROUND_OPACITY,
        CSS_VARIABLES.BACKGROUND_OPACITY,
    ],
]);

export const customizationBackgroundToCssVariable: Record<
    CustomizationBackground,
    CSS_VARIABLES
> = {
    [CustomizationBackground.LOGIN]: CSS_VARIABLES.BACKGROUND_IMAGE_URL,
    [CustomizationBackground.WAITING]: CSS_VARIABLES.CONNECTION_IMAGE_URL,
    [CustomizationBackground.VOTE]: CSS_VARIABLES.VOTE_IMAGE_URL,
};

const imageUrlProps: string[] = [
    CUSTOMIZATION_PROP_LOGO_URL,
    CUSTOMIZATION_PROP_BACKGROUND_URL,
    CUSTOMIZATION_PROP_CONNECTION_IMAGE_URL,
    CUSTOMIZATION_PROP_VOTE_IMAGE_URL,
];

// Percentage to CSS opacity: 0, 100, out of range or not a number all mean full opacity.
export const formatBackgroundOpacity = (
    opacityPercentage?: string | null,
): string => {
    const parsedOpacity = Number(opacityPercentage);
    const isOutOfRange =
        !Number.isFinite(parsedOpacity) ||
        parsedOpacity <= MIN_OPACITY_PERCENTAGE ||
        parsedOpacity >= MAX_OPACITY_PERCENTAGE;

    if (isOutOfRange) {
        return FULL_CSS_OPACITY;
    }

    return String(parsedOpacity / MAX_OPACITY_PERCENTAGE);
};

export interface Customization {
    subdomain: string;
    waitingText: string;
    customization: CustomizationProps;
    footprint: string;
}

export interface Subdomain {
    subdomain: string;
    codeOnline: string;
    url: string;
}

const formatCustomizationValue = (
    field: string,
    value: string | boolean,
): string => {
    if (imageUrlProps.includes(field)) {
        return `url(${value})`;
    }

    if (CUSTOMIZATION_PROP_INSTALLATION_BUTTON_DISPLAY === field) {
        return "flex";
    }

    if (CUSTOMIZATION_PROP_VOTE_BACKGROUND_OPACITY === field) {
        return formatBackgroundOpacity(String(value));
    }

    return String(value);
};

// Must stay pure: mutating props would make a second call produce url(url(...)) and divide the opacity twice.
export const formatCssVariablesFromCustomization = (
    props: CustomizationProps,
) =>
    Object.entries(props)
        // An unmapped property would inject an "undefined: <value>" declaration into the global stylesheet.
        .filter(
            ([field, value]) =>
                customizationPropsToCssVariablesMapping.has(field) && value,
        )
        .map(
            ([field, value]) =>
                `${customizationPropsToCssVariablesMapping.get(
                    field,
                )}: ${formatCustomizationValue(field, value)}`,
        )
        .join("; ") + "; ";

export const setCustomizationLocalStorage = (value: string) => {
    localStorage.setItem(LS_CUSTOMIZATION_KEY, value);
    window.dispatchEvent(new Event(LS_CUSTOMIZATION_SET_EVENT_KEY));
};

export const getCustomizationLocalStorage = (): string | null => {
    return localStorage.getItem(LS_CUSTOMIZATION_KEY);
};

// Reads back an image from the stored customization, the CSS declarations string built by
// formatCssVariablesFromCustomization, where each image is written as "<css variable>: url(<image url>)".
export const getCustomizationBackgroundImageUrl = (
    customizationBackground: CustomizationBackground,
): string | null => {
    const imageCssVariable =
        customizationBackgroundToCssVariable[customizationBackground];
    const imageDeclaration = new RegExp(
        `${imageCssVariable}: url\\((?<imageUrl>[^)]*)\\)`,
    );
    const imageMatch = getCustomizationLocalStorage()?.match(imageDeclaration);
    const imageUrl = imageMatch?.groups?.imageUrl.trim();

    if (!imageUrl) {
        return null;
    }

    return imageUrl;
};

export const setWaitingTextLocalStorage = (text: string) =>
    localStorage.setItem(LS_WAITING_TEXT_KEY, text);

// Bump to invalidate customizations cached by an older front, whose stored CSS variables are no longer the ones we build.
const CUSTOMIZATION_CACHE_VERSION = 1;

export const buildVersionedFootprint = (footprint: string) =>
    `${CUSTOMIZATION_CACHE_VERSION}-${footprint}`;

export const setFootprintLocalStorage = (footprint: string) =>
    localStorage.setItem(LS_FOOTPRINT_KEY, buildVersionedFootprint(footprint));

export const saveCustomization = (customization: Customization) => {
    setWaitingTextLocalStorage(customization.waitingText);

    const cssVars = formatCssVariablesFromCustomization(
        customization.customization,
    );

    setCustomizationLocalStorage(cssVars);

    // Written last: a footprint stored after a failed format would short-circuit every later load without retrying.
    setFootprintLocalStorage(customization.footprint);
};

// Removes the whole cached customization, then notifies listeners so the default theme is displayed back.
export const clearCustomization = () => {
    localStorage.removeItem(LS_CUSTOMIZATION_KEY);
    localStorage.removeItem(LS_FOOTPRINT_KEY);
    localStorage.removeItem(LS_WAITING_TEXT_KEY);
    window.dispatchEvent(new Event(LS_CUSTOMIZATION_SET_EVENT_KEY));
};
